/**
 * Soren Cash Answer Card.
 * Renders 8-week cash forecast detail:
 * - 3 weeks collapsed vs 8 weeks expanded with identical figures.
 * - Closing balance canvas chart on expansion.
 */

import { useState, useRef, useEffect } from 'react'
import type { CashForecastBriefing } from './SorenTodayView'

interface CashWeekRow {
  label: string
  inVal: string
  outVal: string
  closeVal: string
  isNegative: boolean
  isShort?: boolean
}

export function buildCashRows(briefing: CashForecastBriefing): CashWeekRow[] {
  // If we have live week values from the 8-week series:
  const values = briefing.weekValues
  const labels = briefing.weekLabels

  if (values.length === 0 || labels.length === 0) {
    return []
  }

  // Real weekly In/Out from the engine (Cash Report columns). Never net
  // close-to-close — that stuffed payroll into In and printed Out $0.00 (#312).
  const weekIn = briefing.weekIn || []
  const weekOut = briefing.weekOut || []
  return labels.map((lbl, i) => {
    const close = values[i] ?? 0
    let inVal = weekIn[i]
    let outVal = weekOut[i]
    if (inVal == null || outVal == null) {
      const prevClose = i === 0 ? (briefing.bankAmount ?? 0) : (values[i - 1] ?? 0)
      const netChange = close - prevClose
      inVal = netChange >= 0 ? netChange : 0
      outVal = netChange < 0 ? Math.abs(netChange) : 0
    }

    const fmt = (n: number) =>
      `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

    return {
      label: lbl,
      inVal: fmt(inVal),
      outVal: outVal > 0 ? `-${fmt(outVal)}` : '$0.00',
      closeVal: close < 0 ? `-${fmt(close)}` : fmt(close),
      isNegative: close < 0,
      isShort: close < 0,
    }
  })
}

function drawOverlayChart(canvas: HTMLCanvasElement | null, values: number[]) {
  if (!canvas || !canvas.offsetWidth || values.length === 0) return
  const dpr = window.devicePixelRatio || 1
  const w = canvas.offsetWidth
  const h = canvas.offsetHeight
  canvas.width = w * dpr
  canvas.height = h * dpr
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.scale(dpr, dpr)
  ctx.clearRect(0, 0, w, h)

  const p = { l: 8, r: 8, t: 8, b: 8 }
  const minVal = Math.min(-1000, ...values)
  const maxVal = Math.max(10000, ...values)
  const x = (i: number) => p.l + (i * (w - p.l - p.r)) / Math.max(1, values.length - 1)
  const y = (v: number) => p.t + ((maxVal - v) * (h - p.t - p.b)) / Math.max(1, maxVal - minVal)

  ctx.strokeStyle = '#E4E4E0'
  ctx.lineWidth = 1
  ;[0.25, 0.5, 0.75].forEach((r) => {
    const yy = p.t + (h - p.t - p.b) * r
    ctx.beginPath()
    ctx.moveTo(p.l, yy)
    ctx.lineTo(w - p.r, yy)
    ctx.stroke()
  })

  if (minVal < 0 && maxVal > 0) {
    ctx.setLineDash([3, 3])
    ctx.strokeStyle = '#B9BBB6'
    ctx.beginPath()
    ctx.moveTo(p.l, y(0))
    ctx.lineTo(w - p.r, y(0))
    ctx.stroke()
    ctx.setLineDash([])
  }

  ctx.beginPath()
  values.forEach((v, i) => {
    if (i === 0) ctx.moveTo(x(i), y(v))
    else ctx.lineTo(x(i), y(v))
  })
  ctx.strokeStyle = '#3D6B4F'
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  ctx.stroke()

  values.forEach((v, i) => {
    ctx.beginPath()
    const isLowest = v === Math.min(...values)
    ctx.arc(x(i), y(v), isLowest ? 3.8 : 2.5, 0, Math.PI * 2)
    ctx.fillStyle = v < 0 ? '#9A3B2E' : '#FFFFFF'
    ctx.fill()
    ctx.strokeStyle = v < 0 ? '#9A3B2E' : '#3D6B4F'
    ctx.lineWidth = 1.5
    ctx.stroke()
  })
}

export default function SorenCashAnswerCard({
  briefing,
  onFollowups,
  onUpdateBank,
}: {
  briefing: CashForecastBriefing | null
  onFollowups?: () => void
  onUpdateBank?: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const chartRef = useRef<HTMLCanvasElement | null>(null)

  const rows = briefing ? buildCashRows(briefing) : []
  const collapsedRows = rows.slice(0, 3)
  const remainingRows = rows.slice(3)

  useEffect(() => {
    if (!expanded || !briefing?.weekValues?.length) return
    const handle = requestAnimationFrame(() => {
      drawOverlayChart(chartRef.current, briefing.weekValues)
    })
    return () => cancelAnimationFrame(handle)
  }, [expanded, briefing?.weekValues])

  if (!briefing) {
    return (
      <article className="answer-card" data-testid="soren-cash-detail-card">
        <div className="answer-head">
          <span className="answer-kicker">Cash forecast</span>
        </div>
        <p style={{ margin: '12px 0', color: 'var(--ink3, #666)', fontSize: 13, lineHeight: 1.5 }}>
          Cash briefing is not currently available or bank balance is unset.
        </p>
        {onUpdateBank && (
          <div className="actions">
            <button className="btn primary" type="button" onClick={onUpdateBank}>
              Set bank balance
            </button>
          </div>
        )}
      </article>
    )
  }

  return (
    <article className="answer-card" data-testid="soren-cash-detail-card">
      <div className="answer-head">
        <span className="answer-kicker">Cash · next 8 weeks</span>
        <span className="answer-context money">
          bank {briefing.bankFormatted} · {briefing.bankAsOf || 'current'}
        </span>
      </div>

      <div className="metric-grid">
        <div className="metric">
          <span>Lowest point</span>
          <strong
            className={`money ${briefing.lowestPointAmount < 0 ? 'negative' : ''}`}
            data-testid="soren-detail-lowest"
          >
            {briefing.lowestPointFormatted}
          </strong>
          <small>{briefing.lowestWeek ? `week of ${briefing.lowestWeek}` : 'projected'}</small>
        </div>
        <div className="metric">
          <span>Expected in</span>
          <strong className="money" data-testid="soren-detail-expected">
            {briefing.expectedInFormatted}
          </strong>
          <small>next 30 days</small>
        </div>
        <div className="metric">
          <span>Committed out</span>
          <strong className="money" data-testid="soren-detail-committed">
            {briefing.committedOutFormatted || '$0.00'}
          </strong>
          <small>subs, payroll, costs</small>
        </div>
      </div>

      {/* 3 weeks collapsed table */}
      <div className="table-scroll">
        <table className="cash-table" data-testid="soren-cash-table-collapsed">
          <thead>
            <tr>
              <th>Week</th>
              <th>In</th>
              <th>Out</th>
              <th>Close</th>
            </tr>
          </thead>
          <tbody>
            {collapsedRows.map((r, i) => (
              <tr key={i} className={`cash-week-row ${r.isShort ? 'short' : ''}`}>
                <td>{r.label}</td>
                <td>{r.inVal}</td>
                <td>{r.outVal}</td>
                <td className={r.isNegative ? 'negative' : ''}>{r.closeVal}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="verdict" data-testid="soren-detail-verdict">
        <b>{briefing.verdictLine}</b>
      </div>

      <div className="actions">
        {onFollowups && (
          <button className="btn primary" type="button" onClick={onFollowups}>
            Draft follow-ups
          </button>
        )}
        {onUpdateBank && (
          <button className="btn" type="button" onClick={onUpdateBank}>
            Update bank
          </button>
        )}
        <button
          className="btn text"
          type="button"
          data-expand-overlay
          data-testid="soren-cash-expand-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((o) => !o)}
        >
          {expanded ? 'Collapse ▾ 8 weeks + chart' : 'Expand ▸ all 8 weeks + chart'}
        </button>
      </div>

      {expanded && (
        <div className="expanded show" data-expanded data-testid="soren-cash-table-expanded">
          <div className="table-scroll">
            <table className="cash-table">
              <tbody>
                {remainingRows.map((r, i) => (
                  <tr key={i} className={`cash-week-row ${r.isShort ? 'short' : ''}`}>
                    <td>{r.label}</td>
                    <td>{r.inVal}</td>
                    <td>{r.outVal}</td>
                    <td className={r.isNegative ? 'negative' : ''}>{r.closeVal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="chart-wrap">
            <b>Closing balance by week</b>
            <canvas
              ref={chartRef}
              id="overlayChart"
              data-testid="soren-overlay-chart"
              aria-label="Eight-week closing cash balance"
            />
          </div>
        </div>
      )}
    </article>
  )
}
